import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";
import { clientConfig } from "./clientConfigSlice";

const key = visa => visa.id;

export const visaSlice = createSlice({
    name: 'visas',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, visa) => { obj[key(visa)] = visa; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(visa => state.data[key(visa)] = visa);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(visa => delete state.data[key(visa)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => visaSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'visa_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'visa_one');

const fetchAllVisas = network => network.get('/api/v1/visas', visaSlice.actions.set, visualizeFailure('get'));

export const createVisa = (network, visa) => network.post('/api/v1/visa', visa, visaSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateVisa = (network, delta) => network.put('/api/v1/visa', delta, visaSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteVisa = (network, id) => network.delete('/api/v1/visa/' + window.encodeURIComponent(id), visaSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectVisaData = state => state.visas.data;
const selectVisas = createSelector([selectVisaData], data => Object.values(data));
const selectVisa = id => createSelector([selectVisaData], visas => visas[id]);
// #endregion

const listen = {
    visaCreated: (visa, { dispatch, websocket }) => {
        markFetched(websocket, 'visa', key(visa))
        dispatch(visaSlice.actions.addOrUpdate(visa))
    },
    visaUpdated: (visa, { dispatch }) => dispatch(visaSlice.actions.addOrUpdate(visa)),
    visaRemoved: (visa, { dispatch }) => dispatch(visaSlice.actions.remove(visa))
}
const initVisas = { run: fetchAllVisas, listen, immortal: true }
export const [useVisas] = dataHookFactory(selectVisas, initVisas);
export const [useVisa,realizeVisa] = dataHookFactory(selectVisa, id => initVisas);

const fullRoles = ['administrator'];
const readRoles = ['auditor'];
export const canUpdateVisa = (credentials) => credentials.hasRole(fullRoles);
export const canCreateVisa = canUpdateVisa
export const canRemoveVisa = canUpdateVisa;
export const canViewVisa = credentials => credentials.hasRole(fullRoles) || credentials.hasRole(readRoles);
export const canRouteVisa = ({credentials, clientConfig}) => canViewVisa(credentials) && clientConfig.enableVisas;

const reducer = { name: visaSlice.name, reducer: visaSlice.reducer }
export default reducer;